import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Headphones, ShoppingCart, ArrowRight, RefreshCw } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

/**
 * Asks the customer whether they want to add Assisted Deployment to a
 * widget they just clicked "Get this option" on. Renders nothing when
 * pendingItem is null.
 *
 * Props:
 *   pendingItem - object {sku, name, price, widgetFamily, defaultAssisted?,
 *                          replacingName?} describing the item to add. May
 *                 carry over assisted-deployment state from the line being
 *                 replaced.
 *   replacingLineId - lineId of an existing cart line to remove before
 *                     adding (used to enforce one-option-per-widget).
 */
export const AssistedDeploymentModal = ({ pendingItem, replacingLineId, onClose }) => {
  const { addItem, removeItem, assistedPrice } = useCart();
  const navigate = useNavigate();

  if (!pendingItem) return null;

  const addWith = (withAssisted) => {
    if (replacingLineId) {
      removeItem(replacingLineId);
    }
    addItem({
      sku: pendingItem.sku,
      name: pendingItem.name,
      price: pendingItem.price,
      widgetFamily: pendingItem.widgetFamily,
      assistedDeployment: withAssisted,
    });
    const lineTotal = pendingItem.price + (withAssisted ? assistedPrice : 0);
    const heading = replacingLineId
      ? 'Cart updated'
      : withAssisted
        ? 'Added to cart with Assisted Deployment'
        : 'Added to cart';
    toast.success(heading, {
      description: `${pendingItem.name} · $${lineTotal.toFixed(2)}`,
      duration: 2400,
    });
    onClose();
    navigate('/cart');
  };

  const isReplacing = Boolean(replacingLineId && pendingItem.replacingName);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="assisted-deployment-modal"
      onClick={onClose}
    >
      <div
        className="card-surface max-w-lg w-full p-7 sm:p-9 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-faded hover:text-strong"
          data-testid="assisted-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {isReplacing && (
          <div
            className="mb-5 p-3 rounded border border-emphasis flex items-start space-x-3"
            style={{ background: 'rgba(0, 240, 255, 0.05)' }}
            data-testid="assisted-modal-replacing-notice"
          >
            <RefreshCw className="w-4 h-4 accent-cyan shrink-0 mt-0.5" />
            <p className="text-xs text-default leading-relaxed">
              You already have <strong className="text-strong">{pendingItem.replacingName}</strong> in your cart.
              Only one option per widget is allowed — confirming below will
              <strong className="text-strong"> replace it</strong> with the new option.
            </p>
          </div>
        )}

        <div className="flex items-start space-x-4 mb-5">
          <Headphones className="w-7 h-7 accent-green shrink-0 mt-1" />
          <div>
            <span className="eyebrow eyebrow-blue">Add-on</span>
            <h3 className="text-xl sm:text-2xl font-bold mt-2 text-strong tracking-tight">
              Add Assisted Deployment?
            </h3>
          </div>
        </div>

        <p className="text-default text-sm sm:text-base leading-relaxed mb-2">
          {isReplacing ? 'You\'re switching to' : 'You\'re adding'}{' '}
          <strong className="text-strong">{pendingItem.name}</strong>.
        </p>
        <p className="text-muted text-sm leading-relaxed mb-6">
          For <span className="accent-green font-semibold">${assistedPrice}</span>, we'll jump on a 1‑hour
          Zoom call and help you set up the widget in OBS, TikTok LIVE Studio, or your streaming
          platform of choice. Skip the configuration — be live faster.
        </p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={() => addWith(true)}
            className="w-full btn-primary px-5 py-3 inline-flex items-center justify-between"
            data-testid="assisted-modal-add"
          >
            <span className="flex items-center space-x-2">
              <ShoppingCart className="w-4 h-4" />
              <span>Yes, add Assisted Deployment (+${assistedPrice})</span>
            </span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => addWith(false)}
            className="w-full btn-secondary px-5 py-3"
            data-testid="assisted-modal-skip"
          >
            No thanks, just the widget
          </button>
        </div>

        <p className="text-xs text-faded mt-5 text-center">
          You can add or remove Assisted Deployment later from the cart.
        </p>
      </div>
    </div>
  );
};

export default AssistedDeploymentModal;
